import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, AlertCircle, X } from 'lucide-react';
import axios from 'axios';
import Lightbox from '../components/Lightbox';
import { useLightbox } from '../hooks/useLightbox';
import { API } from '../utils/api';

const Desserts = () => {
  const lightbox = useLightbox();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // États pour la modale
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?category=desserts`);
      setProducts(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen">
      <div className="pt-20">
        {/* Hero Section */}
        <section className="italian-section-bg py-20">
          <div className="container mx-auto">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="heading-xl mb-6 text-gray-900">
                Nos Desserts
              </h1>
              <p className="text-lead text-gray-700 mb-8">
                Panettone, pandoro, tiramisu et bien d'autres douceurs pour terminer vos repas en beauté.
              </p>
              <div className="italian-divider"></div>
            </div>
          </div>
        </section>

        {/* Products Section */}
        <section className="section italian-section-alt">
          <div className="container mx-auto">
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-600">Chargement des produits...</p>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">Aucun dessert disponible pour le moment.</p>
                <p className="text-sm text-gray-500">Les produits seront bientôt ajoutés par l'administrateur.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
                {products.map((product) => (
                  <div 
                    key={product.id} 
                    className="card card-hover-elegant cursor-pointer"
                    onClick={() => {
                      setSelectedProduct(product);
                      setShowProductModal(true);
                    }}
                  >
                    <img
                      alt={product.title}
                      className="w-full h-48 object-cover"
                      src={product.image}
                    />
                    <div className="card-body">
                      <h3 className="heading-sm mb-3 text-gray-900">{product.title}</h3>
                      <p className="text-body text-gray-600 mb-4">
                        {product.description}
                      </p>
                      <div className="flex items-center justify-between mb-4">
                        <div className="text-sm font-medium text-green-700 bg-green-50 px-3 py-2 rounded-lg">
                          {product.cookingTimeOrAdvice}
                        </div>
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="section italian-section-bg">
          <div className="container mx-auto text-center">
            <div className="italian-divider mb-8"></div>
            <h2 className="heading-lg mb-6">Envie de découvrir nos autres produits ?</h2>
            <p className="text-lead text-gray-700 mb-8 max-w-2xl mx-auto">
              Explorez notre gamme complète de produits italiens artisanaux
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/pates-fraiches" className="btn btn-primary btn-sm">
                Pâtes fraîches
              </Link>
              <Link to="/fromages" className="btn btn-secondary btn-sm">
                Fromages
              </Link>
              <Link to="/charcuteries" className="btn btn-outline btn-sm">
                Charcuteries
              </Link>
              <Link to="/plats-cuisines" className="btn btn-outline btn-sm">
                Plats cuisinés
              </Link>
            </div>
          </div>
        </section>
      </div>

      {/* Modal Produit */}
      {showProductModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[100000] p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
              <h3 className="text-2xl font-bold text-gray-900">Détails du produit</h3>
              <button
                onClick={() => {
                  setShowProductModal(false);
                  setSelectedProduct(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-8 h-8" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Image en grand */}
              {selectedProduct.image && (
                <div className="w-full rounded-lg overflow-hidden">
                  <img
                    src={selectedProduct.image}
                    alt={selectedProduct.title}
                    className="w-full h-auto max-h-[500px] object-contain bg-gray-50"
                  />
                </div>
              )}

              {/* Titre */}
              <div>
                <h4 className="text-3xl font-bold text-gray-900">{selectedProduct.title}</h4>
              </div>

              {/* Description complète */}
              <div>
                <p className="text-lg text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {selectedProduct.description}
                </p>
              </div>

              {/* Conseil */}
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-500 mb-2">CONSEIL</h4>
                <div className="text-lg font-medium text-green-700 bg-green-50 px-4 py-3 rounded-lg">
                  {selectedProduct.cookingTimeOrAdvice}
                </div>
              </div>

              {/* Bouton fermer */}
              <div className="flex justify-center pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowProductModal(false);
                    setSelectedProduct(null);
                  }}
                  className="px-8 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-lg font-medium"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox.isOpen && (
        <Lightbox
          images={lightbox.images}
          currentIndex={lightbox.currentIndex}
          onClose={lightbox.closeLightbox}
          onPrevious={lightbox.previousImage}
          onNext={lightbox.nextImage}
        />
      )}
    </main>
  );
};

export default Desserts;
